import { filterNullish } from '@modernhealth/filter-nullish';
import { captureException } from 'src/shared/helpers/captureException';
import { loader } from 'graphql.macro';
import { get, isNumber, last, sortBy } from 'lodash';

import { appClient } from 'src/config/apollo';
import { config } from 'src/config/config';
import { httpClient } from 'src/config/http';
import {
    GetAssessmentsDocument,
    GetAssessmentsQuery,
} from '../documents/getAssessmentsQuery.generated';
import {
    SubmitModularizedAssessmentsDocument,
    SubmitModularizedAssessmentsMutation,
    SubmitModularizedAssessmentsMutationVariables,
} from '../documents/submitModularizedAssessmentsMutation.generated';

import { Assessment, MappedAnswers } from '../models/assessments';

const GET_CONTENT_RESPONSES_QUERY = loader('../documents/getContentResponsesQuery.graphql');

interface Who5Survey {
    createdAt: string;
    who5_score: number;
}

// cache go away please
export class AssessmentsService {
    public async submitModularizedAssessments(
        answers: MappedAnswers,
        assessment: {
            name: string;
            assessmentFlowUuid: string;
        },
        careRecommendationUuid?: string,
    ): Promise<{ scores: Record<string, number>; nextAssessment: string | null }> {
        const answersArray = Object.entries(answers);
        const assessmentKey = assessment.name;

        const answersObject = answersArray.reduce((acc, [key, value]) => {
            return {
                ...acc,
                [key]: value,
            };
        }, {});

        const dataToSubmit = {
            [assessmentKey]: answersObject,
        };

        try {
            const { data } = await appClient.mutate<
                SubmitModularizedAssessmentsMutation,
                SubmitModularizedAssessmentsMutationVariables
            >({
                mutation: SubmitModularizedAssessmentsDocument,
                variables: {
                    input: {
                        careRecommendationUuid,
                        content: JSON.stringify(dataToSubmit),
                        assessmentFlowUuid: assessment.assessmentFlowUuid,
                    },
                },
            });

            if (data?.submitModularizedAssessments?.__typename !== 'SuccessfulSubmissionPayload') {
                captureException(new Error('Failed to submit assessment.'), {
                    assessmentKey,
                });

                return {
                    scores: {},
                    nextAssessment: null,
                };
            }

            const scores = {};
            const nextAssessment = data?.submitModularizedAssessments?.nextAssessments?.[0] || null;

            try {
                data?.submitModularizedAssessments?.createdContentResponses?.forEach(
                    ({ delta }) => {
                        if (!delta) return;

                        Object.entries(JSON.parse(delta)).forEach(([key, value]) => {
                            scores[key] = (value as { score: number })['score'];
                        });
                    },
                );
            } catch (error) {
                captureException(error);
            }

            return {
                nextAssessment,
                scores,
            };
        } catch (error) {
            captureException(error);

            return Promise.reject(config.messages.genericError);
        }
    }

    public async fetchScores(): Promise<{
        date: string;
        scores: Record<string, number>;
    }> {
        try {
            const who5Surveys = await httpClient.GET<Who5Survey[]>('survey/who5/');

            const mostRecentWho5Survey = last(
                sortBy(who5Surveys, ({ createdAt }) => new Date(createdAt)),
            );

            const { data } = await appClient.query({
                query: GET_CONTENT_RESPONSES_QUERY,
                fetchPolicy: 'network-only',
            });

            const who5Score = get(mostRecentWho5Survey, 'who5_score');

            const scores = {
                who5: isNumber(who5Score) ? who5Score : -1,
            };

            const content = get(data, 'contentResponses.content');

            if (content) {
                const parsedContent = JSON.parse(content);

                Object.keys(parsedContent).forEach(key => {
                    scores[key] = parsedContent[key].score;
                });
            }

            return {
                date: mostRecentWho5Survey?.createdAt || '',
                scores,
            };
        } catch (error) {
            captureException(error);

            return Promise.reject(config.messages.genericError);
        }
    }

    public async fetchAssessments(): Promise<Assessment[]> {
        try {
            const { data } = await appClient.query<GetAssessmentsQuery>({
                query: GetAssessmentsDocument,
                fetchPolicy: 'network-only',
            });

            return (
                filterNullish(data?.assessments ?? []).map(assessment => ({
                    ...assessment,
                    // TODO: Migrate to using ID or make name required
                    name: assessment.name?.toLowerCase() ?? 'unknown-assessment',
                })) ?? []
            );
        } catch (error) {
            return Promise.reject(config.messages.genericError);
        }
    }
}
